import { FileUpload } from "@/features/files/FileUpload";
import { deleteAttachment, uploadAttachment } from "@repo/api/";
import { Pill, crossIcon } from "@repo/skapa";
import { t } from "@repo/translation";
import { useToast } from "@repo/ui";
import { getErrorMessage, getFileUploadErrorInfo } from "@repo/utils";

import { PillContainer } from "./EmailMultiInput";

interface AttachmentItem {
  id: number;
  fileName: string;
}

interface EmailAttachmentsProps {
  opportunityId: number;
  attachments: AttachmentItem[];
  onChange: (attachments: AttachmentItem[]) => void;
}

export const EmailAttachments = ({
  opportunityId,
  attachments,
  onChange,
}: EmailAttachmentsProps) => {
  const { updateToast } = useToast();
  const onUpload = async (files: FileList) => {
    try {
      const responses = await Promise.all(
        Array.from(files).map((file) => uploadAttachment(opportunityId, file))
      );

      const uploaded: AttachmentItem[] = responses.map((res) => ({
        id: res.id,
        fileName: res.fileName,
      }));

      onChange([...attachments, ...uploaded]);

      updateToast({ title: t("prompt.file_uploaded") });
    } catch (err) {
      const errorInfo = getFileUploadErrorInfo(err);
      updateToast({
        title: errorInfo.title,
        message: errorInfo.message,
        type: "error",
      });
    }
  };

  const handleRemove = async (attachment: AttachmentItem) => {
    const previous = attachments;
    onChange(attachments.filter((a) => a.id !== attachment.id));

    try {
      await deleteAttachment(opportunityId, attachment.id);

      updateToast({
        title: t("prompt.file_deleted"),
      });
    } catch (err) {
      onChange(previous);

      updateToast({
        title: t("prompt.file_deleted_failed"),
        message: getErrorMessage(err),
        type: "error",
      });
    }
  };
  return (
    <>
      <FileUpload
        upload={onUpload}
        accept="image/png, image/jpeg, image/jpg, .pdf, .docx"
      />

      {attachments.length > 0 && (
        <PillContainer>
          {attachments.map((attachment) => (
            <Pill
              key={attachment.id}
              selected
              label={attachment.fileName}
              onClick={() => handleRemove(attachment)}
              trailingIcon={crossIcon}
              size="xsmall"
            />
          ))}
        </PillContainer>
      )}
    </>
  );
};
